"use client"

import type React from "react"

import { createContext, useEffect, useState } from "react"
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut } from "firebase/auth"
import type { User } from "./types"
import { auth } from "./firebase/config"

interface AuthContextType {
  user: User | null
  isAdmin: boolean
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  isLoading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Skip auth state monitoring if auth is not initialized
    if (!auth) {
      setIsLoading(false);
      return () => {};
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get ID token for server session
        const idToken = await firebaseUser.getIdToken()

        // Send the ID token to the server
        await fetch("/api/auth", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ idToken }),
        })

        // Check if user's email matches allowed admin emails
        const isAdmin = firebaseUser.email === 'daniel989@gmail.com' || 
                       firebaseUser.email === 'stellastern989@gmail.com'

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          isAdmin,
        })
      } else {
        setUser(null)
      }

      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      if (!auth) {
        throw new Error('מערכת האימות אינה זמינה כרגע')
      }
      await signInWithEmailAndPassword(auth, email, password)
    } catch (error: any) {
      console.error('Sign in error:', error)
      throw new Error(error.message === 'Firebase: Error (auth/invalid-login-credentials).' 
        ? 'אימייל או סיסמה שגויים'
        : 'שגיאה בהתחברות, נסה שוב'
      )
    }
  }

  const signOut = async () => {
    try {
      if (!auth) {
        throw new Error('מערכת האימות אינה זמינה כרגע')
      }
      await firebaseSignOut(auth)
      // Clear the session cookie
      await fetch("/api/auth", { method: "DELETE" })
    } catch (error) {
      console.error('Sign out error:', error)
      throw new Error('שגיאה בהתנתקות, נסה שוב')
    }
  }

  const signUp = async (email: string, password: string) => {
    try {
      if (!auth) {
        throw new Error('מערכת האימות אינה זמינה כרגע')
      }
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (error: any) {
      console.error('Sign up error:', error)
      if (error.message === 'Firebase: Error (auth/email-already-in-use).') {
        throw new Error('כתובת האימייל כבר קיימת במערכת')
      }
      throw new Error('שגיאה בהרשמה, נסה שוב')
    }
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin: !!user?.isAdmin, isLoading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
